import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";

import usePostRequest from "../../hooks/usePostRequest";
import { HistoryCard } from "./ChangeCards";
import "./ChangeHistory.css";

/**
 * Tabbed change-history popup. Module specifics (tabs, endpoint, payload) come
 * from a thin wrapper such as pages/lifecycle/ChangeHistory/OrderChangeHistoryModal.
 *
 * The endpoint is expected to answer `{ ...basePayload, tab, page, limit }` with
 * `{ data: { items, has_more, counts } }` — see backend
 * controllers/Order/OrderChangeHistoryController.js.
 *
 * @param {boolean}  show
 * @param {Function} onHide
 * @param {string}   title
 * @param {node}     [subtitle]
 * @param {{key: string, label: string, showSection?: boolean}[]} tabs
 *   First tab is the default. Cards show "in <section>" on the "all" tab and on
 *   tabs flagged showSection (tabs that combine several sections).
 * @param {string}   endpoint
 * @param {object}   basePayload
 * @param {number}   [pageSize=20]
 */
function ChangeHistoryModal({
  show,
  onHide,
  title,
  subtitle,
  tabs,
  endpoint,
  basePayload,
  pageSize = 20,
}) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.key);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { postData } = usePostRequest(endpoint);
  /* Ignore responses from a tab the user has already left. */
  const requestSeq = useRef(0);
  const payloadKey = JSON.stringify(basePayload);

  const load = useCallback(
    (tab, nextPage) => {
      const seq = ++requestSeq.current;
      setIsLoading(true);
      setError(null);

      postData(
        { ...basePayload, tab, page: nextPage, limit: pageSize },
        (response) => {
          if (seq !== requestSeq.current) {
            return;
          }
          const data = response?.data || {};
          setItems((current) =>
            nextPage === 1 ? data.items || [] : [...current, ...(data.items || [])]
          );
          setHasMore(Boolean(data.has_more));
          setCounts(data.counts || {});
          setPage(nextPage);
          setIsLoading(false);
        },
        (err) => {
          if (seq !== requestSeq.current) {
            return;
          }
          setError(err?.message || "Could not load the change history.");
          setIsLoading(false);
        },
        endpoint
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payloadKey, endpoint, pageSize]
  );

  useEffect(() => {
    if (show) {
      setItems([]);
      load(activeTab, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, activeTab, payloadKey]);

  const showSection =
    activeTab === "all" ||
    Boolean(tabs.find((tab) => tab.key === activeTab)?.showSection);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      dialogClassName="ch-modal"
    >
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fs-5">{title}</Modal.Title>
          {subtitle && <div className="text-muted small">{subtitle}</div>}
        </div>
      </Modal.Header>

      <Modal.Body className="ch-body">
        <div className="ch-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`ch-tab${activeTab === tab.key ? " active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {counts[tab.key] > 0 && (
                <span className="ch-tab-count">{counts[tab.key]}</span>
              )}
            </button>
          ))}
        </div>

        {error && <div className="ch-state text-danger">{error}</div>}

        {!error && items.length === 0 && isLoading && (
          <div className="ch-state">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading changes…
          </div>
        )}

        {!error && items.length === 0 && !isLoading && (
          <div className="ch-state">No changes recorded here yet.</div>
        )}

        {items.length > 0 && (
          <div className="ch-list">
            {items.map((card) => (
              <HistoryCard key={card.id} card={card} showSection={showSection} />
            ))}
          </div>
        )}

        {hasMore && (
          <div className="ch-load-more">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isLoading}
              onClick={() => load(activeTab, page + 1)}
            >
              {isLoading ? <Spinner animation="border" size="sm" /> : "Load more"}
            </Button>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ChangeHistoryModal;
